import React, { use, useContext, useEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip, Popover, Alert, Card } from "react-bootstrap";
import cx from "classnames";
import { getCSRFToken } from "../utils/csrf";
const CalificacionesContext = React.createContext({});
function filterTreeByKey(tree, targetKey) {
  // Recursively traverse and build the filtered tree
  function filterNode(node) {
    if (node.key === targetKey) {
      return { ...node, children: [] }; // Match found: return node without children
    }

    if (node.children && node.children.length > 0) {
      const filteredChildren = node.children
        .map(filterNode)
        .filter((child) => child !== null);

      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
    }

    return null; // No match in this branch
  }

  return tree.map(filterNode).filter((node) => node !== null);
}

export function PlanillaCalificaciones() {
  const [calificativos, setCalificativos] = useState([]);
  const [prevCalificativos, setPrevCalificativos] = useState([]);
  const [prevEstructura, setPrevEstructura] = useState({});
  const [docente, setDocente] = useState({});
  const [estructura, setEstructura] = useState({});
  const [form_fields, setFormFields] = useState({});

  const [notas, setNotas] = useState(null);
  const [alumnos, setAlumnos] = useState(null);
  const [updatedNotas, setUpdatedNotas] = useState({});
  const [errors, setErrors] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch("api/estructura/").then((res) => res.json()),
      fetch("api/notas/").then((res) => res.json()),
      fetch("api/alumnos/").then((res) => res.json()),
    ]).then(
      ([
        { calificativos, docente, estructura, form_fields },
        notas,
        alumnos,
      ]) => {
        setCalificativos(calificativos);
        setPrevCalificativos(calificativos);

        setDocente(docente);
        setEstructura(estructura);
        setPrevEstructura(estructura);
        setFormFields(form_fields);
        setNotas(notas);
        setAlumnos(alumnos);
      }
    );
  }, []);

  const context = React.useMemo(() => {
    if (!alumnos || !notas || !estructura) return null;

    const form_fields_ix = form_fields?.reduce((acc, cur) => {
      acc[cur.name] = cur;
      return acc;
    }, {});
    const alumnos_ix = alumnos.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {});

    const notas_ix = notas.reduce((acc, cur) => {
      acc[cur.alumno_id] = acc[cur.alumno_id] || {};
      acc[cur.alumno_id][cur.referencia] = cur;
      return acc;
    }, {});

    return {
      errors,
      alumnos,
      notas,
      calificativos,
      docente,
      estructura,
      form_fields,
      alumnos_ix,
      notas_ix,
      form_fields_ix,
    };
  }, [alumnos, notas, calificativos, docente, estructura, form_fields, errors]);

  const handleChange = React.useCallback((nota) => {
    setUpdatedNotas((notas) => {
      notas[nota.alumno_id] = notas[nota.alumno_id] || {};
      notas[nota.alumno_id][nota.referencia] = nota;
      return notas;
    });
  }, []);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    fetch("api/notas/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(
        Object.entries(updatedNotas)
          .map(([alumno_id, notas]) => {
            return Object.entries(notas).map(([referencia, nota]) => {
              return {
                alumno_id,
                referencia,
                ...nota,
              };
            });
          })
          .flat()
      ),
    })
      .then((res) => {
        if (res.status === 200) return res.json();

        return res.json().then((errors) => ({ errors }));
      })
      .then((rs) => {
        setErrors(null);
        if (rs.errors) {
          setErrors(
            rs.errors.reduce((acc, { alumno_id, errors }) => {
              acc[alumno_id] = errors;
              return acc;
            }, {})
          );
        } else {
          setNotas(rs);
          setUpdatedNotas({});
        }
      });
  };
  const handleColToggle = (item, isVertical) => {
    if (isVertical) {
      setEstructura(prevEstructura);
      setCalificativos(prevCalificativos);
    } else {
      setEstructura(filterTreeByKey(prevEstructura, item.key));
      setCalificativos([item.key]);
    }
  };
  return (
    <div>
      {context && (
        <form id="calificaciones-form" onSubmit={handleSubmit}>
          <CalificacionesContext.Provider value={context}>
            <AlertError errors={errors} />
            <CalificacionesTable
              onChange={handleChange}
              onColToggle={handleColToggle}
            />
          </CalificacionesContext.Provider>
        </form>
      )}
    </div>
  );
}

const AlertError = ({ errors }) => {
  const { alumnos_ix } = useContext(CalificacionesContext);

  if (!errors) return null;
  return (
    <Alert variant="warning">
      <dl>
        {Object.entries(errors).map(([alumno_id, errors]) => {
          const alumno = alumnos_ix[alumno_id];
          return (
            <React.Fragment key={alumno_id}>
              <dt>
                <AlumnoName alumno={alumno} />
              </dt>
              {Object.entries(errors).map(([k, v]) => (
                <dd key={k}>
                  <strong>{k}</strong> {v.join(", ")}
                </dd>
              ))}
            </React.Fragment>
          );
        })}
      </dl>
    </Alert>
  );
};

const CeldaNota = ({ calificativo, alumno, onChange }) => {
  const { notas_ix, form_fields_ix } = React.useContext(CalificacionesContext);

  const formField = form_fields_ix[calificativo];
  const nota = notas_ix[alumno.id]?.[calificativo];

  const valor = nota?.valor || "";

  return (
    <td className="p-0">
      <CeldaNotaTooltip valor={valor}>
        <NotaInput
          field={formField}
          calificativo={calificativo}
          alumno={alumno}
          valor={valor}
          onChange={onChange}
        />
      </CeldaNotaTooltip>
    </td>
  );
};

const CeldaNotaTooltip = ({ valor, children }) => {
  return children;
  if (valor.length > 4) {
    return (
      <OverlayTrigger
        trigger={["hover", "focus"]}
        placement="top"
        overlay={<Tooltip id="tooltip-disabled">{valor}</Tooltip>}
      >
        <span>{children}</span>
      </OverlayTrigger>
    );
  }
  return children;
};

const useKeyNavigation = () => {
  return React.useMemo(
    () => ({
      handleKeyDown(e) {
        const isTextarea = e.target.tagName.toLowerCase() === "textarea";

        const td = e.target.closest("td"),
          tr = td.closest("tr");

        const children = Array.from(tr.children);
        const colIx = children.indexOf(td);

        let target = null;
        if (e.shiftKey || e.ctrlKey) return;
        console.log(isTextarea, e.target.selectionStart, e.target.value.length);
        switch (e.keyCode) {
          // Left
          case 37:
            if (isTextarea && e.target.selectionStart > 0) return;

            target = td.previousElementSibling?.querySelector(
              "input,select,textarea"
            );
            break;
          // Up
          case 38:
            if (isTextarea && e.target.selectionStart > 0) return;
            target = tr.previousElementSibling?.children[colIx]?.querySelector(
              "input,select,textarea"
            );

            break;
          // Right
          case 39:
            if (isTextarea && e.target.selectionStart < e.target.value.length)
              return;
            target = td.nextElementSibling?.querySelector(
              "input,select,textarea"
            );

            break;
          // Enter
          case 13:
            if (isTextarea) return;
          // Down
          case 40:
            if (isTextarea && e.target.selectionStart < e.target.value.length)
              return;
            target = tr.nextElementSibling?.children[colIx]?.querySelector(
              "input,select,textarea"
            );

            break;

          default:
            break;
        }
        if (target) {
          e.preventDefault();
          target.focus();
        }
      },

      handleKeyUp(e) {
        const isTextarea = e.target.tagName.toLowerCase() === "textarea";
        if (isTextarea) return;
        switch (e.keyCode) {
          case 37:
          case 38:
          case 39:
          case 40:
            e.target.select();
            e.preventDefault();
            break;
          default:
            break;
        }
      },
    }),
    []
  );
};
const NotaInput = ({
  calificativo,
  alumno,
  valor: initial,
  onChange,
  field,
}) => {
  const [valor, setValor] = useState(initial);
  const handleChange = (e) => {
    console.log(e, e.target.value);

    setValor(e.target.value);

    setTimeout(() => {
      onChange({
        alumno_id: alumno.id,
        referencia: calificativo,
        initial: initial,
        valor: e.target.value,
      });
    }, 100);
  };

  const { errors, calificativos } = useContext(CalificacionesContext);
  const { handleKeyDown, handleKeyUp } = useKeyNavigation();
  const [showPopover, setShowPopover] = useState(false);
  const [isActive, setIsActive] = useState(false);

  let popoverTimeout = null;

  const handleFocus = (e) => {
    setIsActive(true);
    const td = e.target.closest("td");
    const tr = td.closest("tr");
    const cols = td.closest("table").querySelectorAll("col");
    const index = Array.from(tr.children).indexOf(td);
    const col = cols[index];

    col.classList.add("active");
    tr.classList.add("active");

    setShowPopover(true);
  };

  const handleBlur = (e) => {
    const td = e.target.closest("td");
    const tr = td.closest("tr");
    // debugger
    const cols = td.closest("table").querySelectorAll("col");
    const index = Array.from(tr.children).indexOf(td);
    const col = cols[index];

    col.classList.remove("active");
    tr.classList.remove("active");

    popoverTimeout = setTimeout(() => {
      setShowPopover(false);
      setIsActive(false);
    }, 100);
  };

  const handleSubFocus = () => {
    clearTimeout(popoverTimeout);
  };

  const handleSubBlur = () => {
    setShowPopover(false);
  };
  const isSingleCol = calificativos.length === 1;
  let popoverChild = null,
    type = "text",
    attrs = {};
  const isError = errors?.[alumno.id]?.[calificativo];
  const className = {
    "border-primary": isActive,
    "border-warning": valor !== initial,
    "border-danger": isError,
  };
  switch (field?.type) {
    case "ChoiceField":
      popoverChild = (
        <div className={cx("btn-group m-1", className)}>
          {field.choices.map((choice) => (
            <button
              type="button"
              key={choice[0]}
              className={cx("btn btn-sm btn-outline-primary", {
                "btn-outline-warning": valor !== initial,
                "btn-outline-danger": isError,
                active: choice[0] === valor,
              })}
              onClick={() => handleChange({ target: { value: choice[0] } })}
            >
              {choice[1] || "-"}
            </button>
          ))}
        </div>
      );

      break;
    case "CharField":
      if (field.maxlength) {
        popoverChild = (
          <textarea
            onFocus={isSingleCol ? handleFocus : handleSubFocus}
            onBlur={isSingleCol ? handleBlur : handleSubBlur}
            {...(isSingleCol && {
              onKeyDown: handleKeyDown,
              onKeyUp: handleKeyUp,
            })}
            className={cx("form-control h-100 rounded-0", className)}
            rows={6}
            maxLength={field.maxlength}
            value={valor}
            onChange={handleChange}
          />
        );
      }
      break;

    case "IntegerField":
    case "DecimalField":
      type = "number";
      attrs = { min: field.min, max: field.max };

      break;
  }

  const children = (
    <input
      type={type}
      value={valor}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      {...attrs}
      className={cx(
        "form-control m-0 rounded-0 p-1 text-center border-1",
        className,
        { "bg-transparent": !isActive, "fw-bold": isActive }
      )}
    />
  );
  if (isSingleCol) {
    return popoverChild || children;
  }

  return popoverChild ? (
    <OverlayTrigger
      placement="top"
      show={showPopover}
      offset={[-5, 0]}
      overlay={
        <Popover className="custom-popover">
          <Popover.Header as="h3">
            <AlumnoName alumno={alumno} />
          </Popover.Header>
          <Popover.Body className="p-1">{popoverChild}</Popover.Body>
        </Popover>
      }
    >
      {children}
    </OverlayTrigger>
  ) : (
    children
  );
};

const Col = ({ item }) => {
  const ref = useRef();
  const { form_fields_ix } = useContext(CalificacionesContext);
  const form_field = form_fields_ix[item.key];

  useEffect(() => {
    ref.current.setAttribute("style", form_field?.style);
  }, []);

  return <col id={`col-${item.key}`} ref={ref} />;
};
const ThVerticalCol = ({ item, onToggle }) => {
  const [vertical, setVertical] = useState(true);

  const ref = useRef();
  const handleClick = (ev) => {
    const newValue = !vertical;
    setVertical(newValue);
    setTimeout(() => {
      onToggle?.(item, newValue);
    }, 100);
    return;
    const th = ref.current.closest("th");
    const tr = th.closest("tr");
    const table = tr.closest("table");
    const ix = Array.from(tr.querySelectorAll("th")).indexOf(th);

    let prev = tr;
    if (!newValue) {
      while ((prev = prev.previousElementSibling)) {
        let colSpan = 0;
        prev.querySelectorAll("th").forEach((el, i) => {
          const from = colSpan,
            to = colSpan + el.colSpan;
          if (i === 0) {
            colSpan = to;
            return;
          }

          if (ix + 1 > from && ix + 1 < to) {
            el.hidden = false;
          } else {
            el.hidden = true;
          }
          colSpan = to;
        });
      }

      tr.querySelectorAll("th").forEach((el, i) => {
        el.hidden = i !== ix;
      });
      table.querySelectorAll("tbody td").forEach((el, i) => {
        el.hidden = true;
      });

      table
        .querySelectorAll("td:nth-child(" + (ix + 2) + ")")
        .forEach((el, i) => {
          el.hidden = false;
        });
    } else {
      while ((prev = prev.previousElementSibling)) {
        prev.querySelectorAll("th").forEach((el, i) => {
          el.hidden = false;
        });
      }

      tr.querySelectorAll("th").forEach((el, i) => {
        el.hidden = false;
      });
      table.querySelectorAll("tbody td").forEach((el, i) => {
        el.hidden = false;
      });
    }
  };

  return (
    <div
      className={cx(vertical ? "verticaltext-container" : "text-nowrap")}
      onClick={handleClick}
      ref={ref}
    >
      <a
        href="#"
        className={cx(
          "btn btn-link text-decoration-none",
          vertical && "verticaltext-content"
        )}
      >
        {item.label}
      </a>
    </div>
  );
};
const CalificacionesTable = ({ onChange, onColToggle }) => {
  const { estructura, calificativos, docente, alumnos, notas } =
    React.useContext(CalificacionesContext);

  const renderColGroups = () => {
    const colGroups = [
      <colgroup key="alumnos">
        <col className="alumnos" />
      </colgroup>,
    ];

    estructura.forEach((first, i) => {
      const cols = [];

      first.children.forEach((second) => {
        if (second.children && second.children.length > 0) {
          second.children.forEach((third) => {
            cols.push(<Col key={third.key} item={third} />);
          });
        } else {
          cols.push(<Col key={second.key} item={second} />);
        }
      });

      colGroups.push(<colgroup key={first.key}>{cols}</colgroup>);
    });

    return colGroups;
  };

  const renderFirstRow = () => (
    <tr>
      <th rowSpan="3"></th>
      {estructura.map((first, index) => (
        <th
          className="bg-body"
          key={`header-first-${index}`}
          rowSpan={first.rowspan}
          colSpan={first.colspan}
        >
          {first.label}
        </th>
      ))}
    </tr>
  );

  const renderSecondRow = () => (
    <tr>
      {estructura.flatMap((first) =>
        first.children.map((second, index) => (
          <th
            key={second.key}
            rowSpan={second.rowspan}
            colSpan={second.colspan}
          >
            {second.children && second.children.length > 0 ? (
              second.label
            ) : (
              <ThVerticalCol onToggle={onColToggle} item={second} />
            )}
          </th>
        ))
      )}
    </tr>
  );

  const renderThirdRow = () => (
    <tr>
      {estructura.flatMap((first) =>
        first.children.flatMap((second) =>
          second.children.map((third, index) => (
            <th key={third.key} rowSpan={third.rowspan} colSpan={third.colspan}>
              <ThVerticalCol onToggle={onColToggle} item={third} />
            </th>
          ))
        )
      )}
    </tr>
  );
  const renderAlumnos = () => {
    return alumnos.map((alumno) => {
      return (
        <tr key={alumno.id}>
          <AlumnoCelda alumno={alumno} />

          {calificativos.map((calificativo) => {
            return (
              <CeldaNota
                key={calificativo}
                alumno={alumno}
                calificativo={calificativo}
                onChange={onChange}
              />
            );
          })}
        </tr>
      );
    });
  };
  return (
    <Card className="shadow-sm">
      <Card.Body >
        <table className="table table-calificaciones bg-body border-1">
          {renderColGroups()}
          <thead>
            {renderFirstRow()}
            {renderSecondRow()}
            {renderThirdRow()}
          </thead>
          <tbody>{renderAlumnos()}</tbody>
        </table>
      </Card.Body>
    </Card>
  );
};
const AlumnoCelda = (props) => {
  return (
    <th nowrap="nowrap" className="p-1 col-3">
      <AlumnoName {...props} />
    </th>
  );
};

const AlumnoName = ({ alumno }) => {
  const nombre = alumno.usuario?.last_name + " " + alumno.usuario.first_name;
  return nombre;
};
export default CalificacionesTable;
